import { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { AltaExpressForm } from "@/components/AltaExpressForm";
import { toast } from "sonner";
import { Smile, Check, Loader2, LogOut, Clock, Zap } from "lucide-react";

const TOKEN_KEY = "bspark_recepcion_token";

const EMOCIONES = [
  { key: "feliz", emoji: "😀", label: "Feliz" },
  { key: "entusiasmado", emoji: "🤩", label: "Entusiasmado" },
  { key: "tranquilo", emoji: "😌", label: "Tranquilo" },
  { key: "cansado", emoji: "🥱", label: "Cansado" },
  { key: "triste", emoji: "😢", label: "Triste" },
  { key: "enfadado", emoji: "😠", label: "Enfadado" },
  { key: "nervioso", emoji: "😰", label: "Nervioso" },
];

export default function Recepcion() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState("");
  const [pin, setPin] = useState("");
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [ultimo, setUltimo] = useState(null);
  const [eligiendo, setEligiendo] = useState(null); // { sesionId, alumno }
  const [altaOpen, setAltaOpen] = useState(false);
  const [altaDatos, setAltaDatos] = useState({ clases: [], planes: [] });

  const abrirAlta = async () => {
    setAltaOpen(true);
    try {
      const { data } = await cliente().get("/recepcion/alta-datos");
      setAltaDatos(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const altaExpress = async (datos) => {
    try {
      const { data } = await cliente().post("/recepcion/alta-express", datos);
      toast.success(`Alta creada: ${data.matricula}`
        + (data.email_enviado ? " · consentimiento enviado al tutor" : ""));
      setAltaOpen(false);
      cargar();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const cliente = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    axios.get(`${API}/publico/sedes`).then(({ data }) => setSedes(data)).catch(() => {});
  }, []);

  const cargar = async () => {
    if (!token) return;
    try {
      const { data } = await cliente().get("/recepcion/sesiones");
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) salir();
    }
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [token]);

  const entrar = async () => {
    setCargando(true);
    try {
      const { data } = await axios.post(`${API}/recepcion/login`, { sede_id: sedeId, pin });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      setPin("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  };

  const salir = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const marcar = async (emocion) => {
    const { sesionId, alumno } = eligiendo;
    try {
      await cliente().post("/recepcion/checkin", { sesion_id: sesionId, alumno_id: alumno.id, emocion });
      setEligiendo(null);
      setUltimo({ nombre: alumno.nombre, emocion });
      setTimeout(() => setUltimo(null), 3500);
      cargar();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white grid place-items-center p-6" data-testid="recepcion-login">
        <Card className="w-full max-w-md rounded-3xl p-8 bg-neutral-900 border-neutral-800 text-white">
          <h1 className="font-display text-3xl font-extrabold text-white">Modo recepción</h1>
          <p className="text-sm text-neutral-400 mt-2">Elige el centro e introduce el PIN de recepción.</p>
          <div className="mt-7 space-y-4">
            <div className="grid grid-cols-1 gap-2">
              {sedes.map((s) => (
                <button key={s.id} onClick={() => setSedeId(s.id)} data-testid={`recepcion-sede-${s.id}`}
                  className={`text-left px-4 py-3 rounded-xl border text-white transition-colors duration-200 ${
                    sedeId === s.id ? "border-pink-500 bg-pink-500/10" : "border-neutral-800 hover:border-neutral-600"}`}>
                  <span className="font-semibold">{s.nombre}</span>
                  <span className="text-neutral-400 text-sm"> · {s.ciudad}</span>
                </button>
              ))}
            </div>
            <Input type="password" inputMode="numeric" placeholder="PIN" value={pin} maxLength={6}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="h-14 text-center text-2xl tracking-[0.5em] bg-neutral-950 border-neutral-800 text-white placeholder:text-neutral-500"
              data-testid="recepcion-pin-input" />
            <Button className="w-full h-12 rounded-full font-semibold text-base" disabled={!sedeId || pin.length < 4 || cargando}
              onClick={entrar} data-testid="recepcion-entrar-btn">
              {cargando ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrar"}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-6 sm:p-10" data-testid="recepcion-page">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">¡Hola! ¿Ya has llegado?</h1>
          <p className="text-neutral-400 mt-1 text-sm">
            {data?.sede?.nombre} · Pulsa tu nombre para marcar la asistencia. Cada turno muestra solo
            a sus alumnos; abajo tienes el resto de turnos de hoy.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full border-neutral-700 bg-transparent text-white hover:text-white hover:bg-neutral-800"
            onClick={abrirAlta} data-testid="recepcion-alta-express-btn">
            <Zap className="h-4 w-4 mr-2" /> Alta express
          </Button>
          <Button variant="outline" className="rounded-full border-neutral-700 bg-transparent text-white hover:text-white hover:bg-neutral-800"
            onClick={salir} data-testid="recepcion-salir-btn">
            <LogOut className="h-4 w-4 mr-2" /> Salir
          </Button>
        </div>
      </div>

      {altaOpen && (
        <div className="mb-10 rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6"
          data-testid="recepcion-alta-express">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h2 className="font-display text-2xl font-bold">Alta express</h2>
            <button onClick={() => setAltaOpen(false)} className="text-sm text-neutral-400"
              data-testid="recepcion-alta-cerrar">Cerrar</button>
          </div>
          <AltaExpressForm clases={altaDatos.clases} planes={altaDatos.planes} oscuro
            onEnviar={altaExpress} />
        </div>
      )}

      {ultimo && (
        <div className="mb-8 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 px-6 py-5 flex items-center gap-4"
          data-testid="recepcion-confirmacion">
          <span className="text-4xl leading-none">
            {EMOCIONES.find((e) => e.key === ultimo.emocion)?.emoji || "🎨"}
          </span>
          <div>
            <p className="font-display text-2xl font-bold text-emerald-300">¡Bienvenido/a, {ultimo.nombre}!</p>
            <p className="text-sm text-emerald-200/80 mt-1">
              Gracias por contarnos cómo te sientes hoy.
            </p>
          </div>
        </div>
      )}

      {(data?.sesiones || []).map((s) => (
        <div key={s.id} className={`mb-12 ${s.en_curso ? "" : "opacity-60"}`}
          data-testid={`recepcion-sesion-${s.id}`}>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <Clock className={`h-5 w-5 ${s.en_curso ? "text-pink-500" : "text-neutral-500"}`} />
            <h2 className="font-display text-lg md:text-lg font-bold">{s.clase}</h2>
            <span className="text-neutral-500 text-sm">{s.hora_inicio} – {s.hora_fin}</span>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              s.en_curso ? "bg-pink-500/15 text-pink-400" : "bg-neutral-800 text-neutral-400"}`}
              data-testid={`recepcion-estado-${s.id}`}>
              {s.en_curso ? "Ahora" : "Otro turno de hoy"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
            {s.alumnos.map((a) => {
              const marcado = a.estado === "presente" || a.estado === "reposicion";
              return (
                <button key={a.id} disabled={marcado} onClick={() => setEligiendo({ sesionId: s.id, alumno: a })}
                  data-testid={`recepcion-alumno-${a.id}`}
                  className={`rounded-3xl p-6 text-left text-white transition-transform duration-200 border ${
                    marcado
                      ? "bg-emerald-500/15 border-emerald-500/40"
                      : "bg-neutral-900 border-neutral-800 hover:-translate-y-1 hover:border-pink-500"}`}>
                  {marcado
                    ? <Check className="h-10 w-10 text-emerald-400" />
                    : <Smile className="h-10 w-10 text-pink-500" />}
                  <p className="font-display text-xl font-bold mt-4 leading-tight">{a.nombre}</p>
                  <p className="text-xs text-neutral-400 mt-1">
                    {marcado
                      ? `Asistencia marcada${a.emocion ? ` · ${EMOCIONES.find((e) => e.key === a.emocion)?.emoji || ""}` : ""}`
                      : "Pulsa para marcar"}
                  </p>
                </button>
              );
            })}
            {s.alumnos.length === 0 && <p className="text-neutral-500 text-sm">Sin alumnos en esta clase.</p>}
          </div>
        </div>
      ))}

      {data && (data.sesiones || []).length === 0 && (
        <p className="text-neutral-400" data-testid="recepcion-vacio">
          Hoy no hay clases programadas en este centro.
        </p>
      )}

      {eligiendo && (
        <div className="fixed inset-0 z-50 bg-neutral-950/95 backdrop-blur-sm grid place-items-center p-6"
          data-testid="emociones-dialog">
          <div className="w-full max-w-3xl">
            <p className="text-center text-neutral-400 text-sm">{eligiendo.alumno.nombre}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-center text-white mt-2">
              ¿Cómo te sientes hoy?
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-9">
              {EMOCIONES.map((e) => (
                <button key={e.key} onClick={() => marcar(e.key)} data-testid={`emocion-${e.key}`}
                  className="rounded-3xl bg-neutral-900 border border-neutral-800 py-7 text-white
                    hover:border-pink-500 hover:-translate-y-1 transition-transform duration-200">
                  <span className="text-5xl leading-none block">{e.emoji}</span>
                  <span className="font-display font-bold text-lg mt-3 block">{e.label}</span>
                </button>
              ))}
            </div>
            <button onClick={() => setEligiendo(null)} data-testid="emociones-cancelar"
              className="block mx-auto mt-9 text-neutral-400 text-sm hover:text-white transition-colors duration-200">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
